import {
  CalloutComponent,
  FormValues,
  getValue,
  InputFormCallout,
  InputFormGroup,
  IntegerInput,
  SubmitButtonMode,
} from "o4a-react";
import * as React from "react";
import apiClients from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { CalloutTestIds } from "../../constants/uiConstants";
import { parseId } from "../../helpers/idHelper";
import { mdsDataStorageSizeMaxValue, mdsDataStorageSizeMinValue } from "../../helpers/resourceHelper";
import { useMutationCall } from "../../hooks/useMutationCall";
import {
  AsyncNotificationMethodKey,
  AsyncNotificationPolledResponseKey,
  MDS_UPDATE_POLL_DELAY,
  MDS_UPDATE_POLL_INTERVAL,
} from "../../models/AsyncNotificationProviders";
import { getOciRegion } from "../../utils";
import { OperationActionProps } from "../OperationTypes";

export enum Fields {
  DataStorageSize = "dataStorageSize",
}

export enum FieldTestIds {
  DataStorageSize = "dataStorageSize",
}

export interface MysqlEditStorageProps extends OperationActionProps {
  databaseId: string | undefined;
  dataStorageSizeInGBs: number | undefined;
}

const MysqlEditStorage = (
  { targetId, location, databaseId, dataStorageSizeInGBs, onExecute, onCancel }: MysqlEditStorageProps,
): JSX.Element => {
  const { subscriptionId, resourceGroup, resourceName } = parseId(databaseId);
  const resourceGroupName = decodeURIComponent(resourceGroup || "");
  const dbSystemName = decodeURIComponent(resourceName || "");

  const [initialValues, setInitialValues] = React.useState<FormValues>();

  React.useEffect(() => {
    // eslint-disable-next-line eqeqeq
    if (dataStorageSizeInGBs != undefined) {
      const initFieldValues: FormValues = {
        // eslint-disable-next-line eqeqeq
        [Fields.DataStorageSize]: dataStorageSizeInGBs != undefined
          ? Number(dataStorageSizeInGBs).toString()
          : undefined,
      };
      setInitialValues(initFieldValues);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataStorageSizeInGBs]);

  const [calloutRef, setCalloutRef] = React.useState<CalloutComponent>({} as CalloutComponent);
  const { invokeCall: invokeMdsUpdate } = useMutationCall(
    { method: apiClients.withRegion(getOciRegion(location)).mdsDatabaseApi.updateMdsDbSystem },
  );

  const onUpdate = (formValues: FormValues): void => {
    const newStorageSize = getValue<string>(formValues, Fields.DataStorageSize, InputFormGroup);
    const storageSize = newStorageSize ? parseInt(newStorageSize, 10) : undefined;

    const invokeOptions = {
      onSuccess: onExecute,
      onFailure: calloutRef?.allowResubmit,
      notification: {
        inProgress: {
          title: Messages.notifications.inProgress.titles.updateMysqlDataStorageSize(),
          message: Messages.notifications.inProgress.messages.updateMysqlDataStorageSize(dbSystemName),
        },
        success: {
          title: Messages.notifications.success.titles.updateMysqlDataStorageSize(),
          message: Messages.notifications.success.messages.updateMysqlDataStorageSize(dbSystemName),
        },
        failure: {
          title: Messages.notifications.failure.titles.updateMysqlDataStorageSize(),
          message: Messages.notifications.failure.messages.updateMysqlDataStorageSize(dbSystemName),
        },
        asyncPolling: {
          methodKey: AsyncNotificationMethodKey.MDS_GET,
          methodArgs: {
            subscriptionId,
            resourceGroupName,
            dbSystemName,
          },
          location,
          responseMethodKey: AsyncNotificationPolledResponseKey.MDS_UPDATED_CHECK,
          delay: MDS_UPDATE_POLL_DELAY,
          interval: MDS_UPDATE_POLL_INTERVAL,
        },
      },
    };
    invokeMdsUpdate({
      subscriptionId,
      resourceGroupName,
      dbSystemName,
      updateMdsDbSystemDetails: { dataStorageSizeInGBs: storageSize },
    }, invokeOptions);
  };

  return (
    <InputFormCallout
      targetId={targetId}
      title={Messages.createNewPanels.editDataStorageSize.title()}
      componentRef={setCalloutRef}
      submitButtonMode={SubmitButtonMode.DISABLE_TILL_VALID}
      onSubmit={onUpdate}
      onClose={onCancel}
      initialValues={initialValues}
      testId={CalloutTestIds.MysqlEditStorage}
    >
      <IntegerInput
        testId={FieldTestIds.DataStorageSize}
        fieldName={Fields.DataStorageSize}
        label={Messages.labels.dataStorageSize()}
        defaultValue={dataStorageSizeInGBs}
        min={dataStorageSizeInGBs || mdsDataStorageSizeMinValue}
        max={mdsDataStorageSizeMaxValue}
        required
      />
    </InputFormCallout>
  );
};

export const newMysqlEditStorage = (
  props: MysqlEditStorageProps,
): JSX.Element => (<MysqlEditStorage {...props} />);
